import { NotificationType } from 'hooks/useNotification';
import type { OrgEventNotifyConfig } from 'hooks/orgEvents/orgEventAutomationHooks';

export const MICROSOFT_TEAMS_LOCK_TOOLTIP = 'Upgrade to Enable';

export const AUTOMATION_CHANNEL_OPTIONS = [
  { label: 'PRATUS (in-app)', value: NotificationType.IN_APP },
  { label: 'Email', value: NotificationType.EMAIL },
  { label: 'SMS', value: NotificationType.SMS },
  {
    label: 'Microsoft Teams',
    value: NotificationType.TEAMS,
    disabled: true,
    tooltip: MICROSOFT_TEAMS_LOCK_TOOLTIP,
  },
  {
    label: 'Voice (TTS cell)',
    value: NotificationType.TTS_CELL,
    disabled: true,
    tooltip: MICROSOFT_TEAMS_LOCK_TOOLTIP,
  },
] as const;

/** Channels the picker shows but will not save. */
export function withoutLockedChannels(
  types: readonly string[] | undefined
): string[] {
  const locked = new Set<string>(
    AUTOMATION_CHANNEL_OPTIONS.filter(
      (option) => 'disabled' in option && option.disabled
    ).map((option) => option.value)
  );
  return (types ?? []).filter((type) => !locked.has(type));
}

export const AUTOMATION_PRIORITY_OPTIONS = [
  { label: 'Info', value: 'info' },
  { label: 'Advisory', value: 'advisory' },
  { label: 'Watch', value: 'watch' },
  { label: 'Warning', value: 'warning' },
  { label: 'Critical', value: 'critical' },
  { label: 'Stand Down Safety', value: 'stand_down_safety' },
] as const;

export type AutomationNotifyFormFields = {
  notifyOnCreate: boolean;
  notificationTypes: string[];
  userIds: string[];
  teamIds: string[];
  roleScopeKeys: string[];
  priority: string;
  confirmReceipt: boolean;
};

export function notifyFieldsFromRule(rule: {
  notify_on_create?: boolean;
  notify_config?: OrgEventNotifyConfig | null;
}): AutomationNotifyFormFields {
  const notificationTypes = withoutLockedChannels(
    rule.notify_config?.notificationTypes
  );
  return {
    notifyOnCreate: Boolean(rule.notify_on_create),
    notificationTypes: notificationTypes.length
      ? notificationTypes
      : [NotificationType.IN_APP],
    userIds: rule.notify_config?.userIds ?? [],
    teamIds: rule.notify_config?.teamIds ?? [],
    roleScopeKeys: rule.notify_config?.roleScopeKeys ?? [],
    priority: rule.notify_config?.priority || 'watch',
    confirmReceipt: Boolean(rule.notify_config?.confirmReceipt),
  };
}

export function buildNotifyConfigPayload(
  values: AutomationNotifyFormFields,
  existing?: OrgEventNotifyConfig | null
): OrgEventNotifyConfig {
  const notificationTypes = withoutLockedChannels(values.notificationTypes);
  return {
    ...existing,
    notificationTypes: notificationTypes.length
      ? notificationTypes
      : [NotificationType.IN_APP],
    userIds: values.userIds ?? [],
    teamIds: values.teamIds ?? [],
    roleScopeKeys: values.roleScopeKeys ?? [],
    priority: values.priority || 'watch',
    confirmReceipt: Boolean(values.confirmReceipt),
    titleTemplate: existing?.titleTemplate || 'Threshold event: {eventName}',
    bodyTemplate:
      existing?.bodyTemplate ||
      '{eventName}: {stormName} impacts {resourceCount} resources.',
  };
}
